import { useState, useEffect, useRef } from "react";
import { FaSearch, FaTimes, FaUser } from "react-icons/fa";
import useSearchUsersForGift from "api/UserResource/useSearchUsersForGift";

const RecipientSearch = ({ selectedRecipient, onSelectRecipient, onClearRecipient }) => {
	const [searchQuery, setSearchQuery] = useState("");
	const [showDropdown, setShowDropdown] = useState(false);
	const [debouncedQuery, setDebouncedQuery] = useState("");
	const wrapperRef = useRef(null);

	const { data: searchResults, isLoading } = useSearchUsersForGift(debouncedQuery, debouncedQuery.length > 0);

	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedQuery(searchQuery);
		}, 300);

		return () => clearTimeout(timer);
	}, [searchQuery]);

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
				setShowDropdown(false);
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, []);

	const handleSelect = (user) => {
		onSelectRecipient(user);
		setSearchQuery("");
		setShowDropdown(false);
	};

	const handleClear = () => {
		onClearRecipient();
		setSearchQuery("");
		setShowDropdown(false);
	};

	const hasResults = searchResults?.data && searchResults.data.length > 0;

	if (selectedRecipient) {
		return (
			<div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center justify-between">
				<div className="flex items-center gap-3">
					<div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
						<FaUser className="text-green-600 text-sm" />
					</div>
					<div>
						<p className="text-sm font-medium text-gray-900">{selectedRecipient.full_name}</p>
						<p className="text-xs text-gray-500">{selectedRecipient.email}</p>
					</div>
				</div>
				<button
					type="button"
					onClick={handleClear}
					className="text-gray-400 hover:text-gray-600 transition-colors"
				>
					<FaTimes />
				</button>
			</div>
		);
	}

	return (
		<div ref={wrapperRef} className="relative">
			<div className="relative">
				<div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
					{isLoading ? (
						<div className="animate-spin">
							<svg className="h-4 w-4 text-gray-400" viewBox="0 0 24 24">
								<circle
									className="opacity-25"
									cx="12"
									cy="12"
									r="10"
									stroke="currentColor"
									strokeWidth="4"
									fill="none"
								/>
								<path
									className="opacity-75"
									fill="currentColor"
									d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
								/>
							</svg>
						</div>
					) : (
						<FaSearch className="h-4 w-4 text-gray-400" />
					)}
				</div>
				<input
					type="text"
					value={searchQuery}
					onChange={(e) => {
						setSearchQuery(e.target.value);
						setShowDropdown(true);
					}}
					onFocus={() => setShowDropdown(true)}
					placeholder="Search by name, email, or username"
					className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-akgreen/20 focus:border-akgreen transition-colors"
				/>
			</div>

			{showDropdown && searchQuery.length > 0 && (
				<div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
					{isLoading ? (
						<div className="p-3 text-center text-gray-500 text-sm">
							Searching...
						</div>
					) : !hasResults ? (
						<div className="p-3 text-center text-gray-500 text-sm">
							No users found
						</div>
					) : (
						<ul>
							{searchResults.data.map((user) => (
								<li key={user.id}>
									<button
										type="button"
										onClick={() => handleSelect(user)}
										className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors flex items-center gap-3"
									>
										<div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
											<FaUser className="text-gray-500 text-sm" />
										</div>
										<div className="min-w-0 flex-1">
											<p className="text-sm font-medium text-gray-900 truncate">
												{user.full_name}
											</p>
											<p className="text-xs text-gray-500 truncate">
												{user.email}
											</p>
										</div>
									</button>
								</li>
							))}
						</ul>
					)}
				</div>
			)}
		</div>
	);
};

export default RecipientSearch;
